import { CopropertyDto, CopropertyEligibilityStatus, UpdateCopropertyClaimsHistoryCommand } from 'api/coproperty';
import { CopropertyClaimsForm } from 'features/subscription-process/coproperty/CopropertyClaimsForm';
import { usePanel } from 'hooks/usePanel';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { copropertiesApi } from 'services/swagger-api';
import { useRefetchQuoteData } from '../../../hooks/useRefetchQuoteData';

interface EditCopropertyClaimsPanelProps {
  /** Copropriété du devis, telle que la contractualisation la renvoie. */
  coproperty: CopropertyDto;
  /** La situation de sinistralité doit être renseignée pour enregistrer (contractualisation). */
  choiceRequired?: boolean;
}

/**
 * Sous-panneau « Sinistralité déclarée » de la contractualisation MRI : la question de
 * situation du parcours — obligatoire ici, puisque c'est elle qui débloque l'envoi du
 * contrat —, la grille des sinistres quand il y en a, l'historique d'assurance et la
 * renonciation à recours réciproque (pré-remplis depuis la lecture de la sinistralité).
 */
export const EditCopropertyClaimsPanel: FC<EditCopropertyClaimsPanelProps> = ({ coproperty, choiceRequired }) => {
  const { closePanel, setPanelActions } = usePanel();
  const refetchQuoteData = useRefetchQuoteData();
  const [isSaving, setIsSaving] = useState(false);

  const buildCommandRef = useRef<(() => UpdateCopropertyClaimsHistoryCommand | null) | null>(null);
  const registerBuildCommand = useCallback((build: () => UpdateCopropertyClaimsHistoryCommand | null) => {
    buildCommandRef.current = build;
  }, []);

  const handleSubmit = useCallback(async () => {
    const command = buildCommandRef.current?.();
    if (!command || !coproperty.id) return;

    setIsSaving(true);
    try {
      const response = await copropertiesApi.updateCopropertyClaimsHistory({
        copropertyId: coproperty.id,
        updateCopropertyClaimsHistoryCommand: command
      });

      if (!response.isSuccess) {
        toast.error(response.errorMessage || "L'enregistrement de la sinistralité a échoué.");
        return;
      }

      // Seul un refus est signalé ici (ex. renonciation à recours refusée) ; le reste du verdict est ignoré.
      if (response.eligibility?.status === CopropertyEligibilityStatus.NonEligible) {
        toast.error(response.eligibility.reasons.join(' ') || 'Cette copropriété ne peut pas être assurée en MRI.');
      }

      await refetchQuoteData();
      closePanel();
    } catch (error) {
      console.error(error);
      toast.error('Une erreur est survenue.');
    } finally {
      setIsSaving(false);
    }
  }, [coproperty.id, refetchQuoteData, closePanel]);

  useEffect(() => {
    setPanelActions({
      closeLabel: 'Annuler',
      submitLabel: isSaving ? 'Enregistrement…' : 'Enregistrer',
      submitDisabled: isSaving,
      onSubmit: handleSubmit
    });
  }, [setPanelActions, handleSubmit, isSaving]);

  return (
    <div className="flex flex-col gap-6">
      <CopropertyClaimsForm
        coproperty={coproperty}
        registerBuildCommand={registerBuildCommand}
        choiceRequired={choiceRequired}
      />
    </div>
  );
};
